import {
  DecimalUtil,
  Percentage,
} from '@orca-so/common-sdk'
import type { TokenInfo, Whirlpool } from '@renec-foundation/nemoswap-sdk'
import {
  increaseLiquidityQuoteByInputToken,
  PriceMath,
} from '@renec-foundation/nemoswap-sdk'
import Decimal from 'decimal.js'
import {
  Formik,
  type FormikErrors,
  type FormikProps,
} from 'formik'
import { useRouter } from 'next/router'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useBalances } from '@/hooks/use-balances'
import useNemoWallet from '@/hooks/use-nemo-wallet'
import useSafelySendTxWithNotice from '@/hooks/use-safely-send-tx-with-notice'
import { useRemitanoRate } from '@/hooks/use-settings'
import {
  type PairInfo,
  useTokens,
} from '@/hooks/use-tokens'
import useTrans from '@/hooks/useTrans'
import { useNemoProgram } from '@/providers/nemo-program'
import { usePools } from '@/providers/pool'
import {
  toResult,
} from '@/utils/helpers'
import { getTokensRate } from '@/utils/helpers.liquidity'
import { formatRange, toDecimal } from '@/utils/numeric'

import type {
  OpenPositionContainerProps,
  OpenPositionFormValues,
} from './types'
import usePriceRange from './use-price-range'
import { getInitTickArrayTx } from './utils'
import View from './view'
import ViewSkeleton from './view.skeleton'

const SLIPPAGE = Percentage.fromDecimal(new Decimal(1))
const DEFAULT_MODIFYING_RATE = 1 - 0.08 // range 8%

const Container = (props: OpenPositionContainerProps) => {
  const {
    poolAddress,
  } = props
  const trans = useTrans()
  const { getPools, pools: allPools } = usePools('none')
  const { client } = useNemoProgram()

  useEffect(() => {
    getPools([poolAddress], { refreshMetadata: true, refreshWhirlpool: true })
  }, [getPools, poolAddress])

  const { data: rateData } = useRemitanoRate()
  const { connectWallet, publicKey } = useNemoWallet()
  const { balances: userBalances } = useBalances()
  const { safelySendTxWithNotice } = useSafelySendTxWithNotice('create_position')
  const router =  useRouter()
  const { getTokenInfo, renecTokenInfo, reUsdTokenInfo } = useTokens()

  const formRef = useRef<FormikProps<OpenPositionFormValues>>(null)

  const [currentPool, currentPair] = useMemo<[Whirlpool | null, PairInfo | null]>(() => {
    const { whirlpool } = allPools.find(p => p.whirlpool?.getAddress().toBase58() === poolAddress) ?? {}
    if (whirlpool) {
      const whirlPoolData = whirlpool.getData()
      const tokenA = getTokenInfo(whirlPoolData.tokenMintA.toBase58())
      const tokenB = getTokenInfo(whirlPoolData.tokenMintB.toBase58())
      if (tokenA && tokenB) {

        const pair: PairInfo = [
          { ...tokenA, nickName: 'tokenA', decimals: whirlpool.getTokenAInfo().decimals },
          { ...tokenB, nickName: 'tokenB', decimals: whirlpool.getTokenBInfo().decimals },
        ]
        return [whirlpool, pair]
      }
    }

    return [null, null]
  }, [
    allPools,
    poolAddress,
    getTokenInfo,
  ])

  const bPerAPrice = useMemo(
    () =>
      currentPool &&
      PriceMath.sqrtPriceX64ToPrice(
        currentPool.getData().sqrtPrice,
        currentPool.getTokenAInfo().decimals,
        currentPool.getTokenBInfo().decimals,
      ),
    [currentPool],
  )

  const { priceRange, tickRange, setPriceRange } = usePriceRange(currentPool ?? undefined)
  const [tokenIn, tokenOut] = useMemo(() => (currentPair || []), [currentPair])
  const [fieldNameOfMaxAmount, setFieldNameOfMaxAmount] = useState<keyof OpenPositionFormValues>('tokenAmountA')
  const [maxAmount, setMaxAmount] = useState<Decimal>(new Decimal(0))
  const [pricesReverted, setPricesReverted] = useState(false)
  const [isOpenSetLiquidity, setIsOpenSetLiquidity] = useState(false)

  const [usdRateA, usdRateB] = useMemo(() => {
    return (
      (currentPool &&
        (getTokensRate({
          pool: currentPool,
          tokenAddress: reUsdTokenInfo.address,
          tokenUSDRate: rateData?.REUSD || 0,
        }) ||
          getTokensRate({
            pool: currentPool,
            tokenAddress: renecTokenInfo.address,
            tokenUSDRate: rateData?.RENEC || 0,
          }))) || [new Decimal(0), new Decimal(0)]
    )
  }, [currentPool, renecTokenInfo, reUsdTokenInfo, rateData])

  const insufficientBalanceMsg = useMemo(
    () => publicKey && new Decimal(userBalances['RENEC'] || 0).lte(0) ? trans.home.insufficient_balance : '',
    [publicKey, trans.home.insufficient_balance, userBalances],
  )

  const ratios = useMemo<[Decimal, Decimal] | null>(() => {
    if (currentPool && tickRange) {
      const tokenAInfo = currentPool.getTokenAInfo()
      const tokenBInfo = currentPool.getTokenBInfo()
      const { tickCurrentIndex } = currentPool.getData()
      const [tickLowerIndex, tickUpperIndex] = tickRange

      let inputToken = tokenAInfo
      if (tickLowerIndex <= tickCurrentIndex) {
        inputToken = tokenBInfo
      }

      const [quoteErr, quote] = toResult(() => increaseLiquidityQuoteByInputToken(
        inputToken.mint,
        new Decimal(1),
        tickLowerIndex,
        tickUpperIndex,
        SLIPPAGE,
        currentPool,
      ))
      if (quote) {
        // console.log('quote.liquidityAmount', DecimalUtil.fromU64(quote.liquidityAmount, tokenAInfo.decimals).toString())
        // console.log('quote.tokenEstA', DecimalUtil.fromU64(quote.tokenEstA, tokenAInfo.decimals).toString())
        // console.log('quote.tokenEstB', DecimalUtil.fromU64(quote.tokenEstB, tokenBInfo.decimals).toString())
        // console.log('quote.tokenMaxA', DecimalUtil.fromU64(quote.tokenMaxA, tokenAInfo.decimals).toString())
        // console.log('quote.tokenMaxB', DecimalUtil.fromU64(quote.tokenMaxB, tokenBInfo.decimals).toString())

        const tokenAEstAmount = DecimalUtil.fromU64(quote.tokenEstA, tokenAInfo.decimals)
        const tokenBEstAmount = DecimalUtil.fromU64(quote.tokenEstB, tokenBInfo.decimals)

        const tokenMaxValueA = tokenAEstAmount.times(usdRateA)
        const tokenMaxValueB = tokenBEstAmount.times(usdRateB)

        const total = tokenMaxValueA.plus(tokenMaxValueB)
        if (total.eq(0)) return null

        const ratioA = tokenMaxValueA.div(total)
        const ratioB = tokenMaxValueB.div(total)
        return [ratioA, ratioB]
      } else {
        console.log('quoteErr', quoteErr)
      }
    }
    return null
  }, [currentPool, tickRange, usdRateA, usdRateB])

  const initialValues = useMemo(() => ({
    minPrice: '',
    maxPrice: '',
    tokenAmountA: 0,
    tokenAmountB: 0,
  }), [])

  useEffect(() => {
    if (currentPool) {
      const { sqrtPrice } = currentPool.getData()
      const tokenAInfo = currentPool.getTokenAInfo()
      const tokenBInfo = currentPool.getTokenBInfo()
      const bPerAPrice = PriceMath.sqrtPriceX64ToPrice(
        sqrtPrice,
        tokenAInfo.decimals,
        tokenBInfo.decimals,
      )

      const lowerPrice = bPerAPrice.times(DEFAULT_MODIFYING_RATE)
      const upperPrice = bPerAPrice.div(DEFAULT_MODIFYING_RATE)
      setPriceRange([lowerPrice, upperPrice])
    }
  }, [currentPool, setPriceRange])

  const orderedPriceRange = useMemo<[Decimal, Decimal]>(() => {
    const [minPrice, maxPrice] = priceRange

    const orderedMinPrice = pricesReverted ? maxPrice.pow(-1) : minPrice
    const orderedMaxPrice = pricesReverted ? minPrice.pow(-1) : maxPrice

    const finiteMinPrice = orderedMinPrice.isFinite() ? orderedMinPrice : new Decimal(0)
    const finiteMaxPrice = orderedMaxPrice.isFinite() ? orderedMaxPrice : new Decimal(0)

    return [finiteMinPrice, finiteMaxPrice]
  }, [priceRange, pricesReverted])

  const [formattedMinPrice, formattedMaxPrice] = useMemo(
    () =>
      tokenIn && tokenOut
        ? formatRange(orderedPriceRange, pricesReverted ? tokenIn.decimals : tokenOut.decimals)
        : ['', ''],
    [orderedPriceRange, pricesReverted, tokenIn, tokenOut],
  )

  const quoteByToken = useCallback((token: TokenInfo, amount: Decimal) => {
    if (currentPool && tickRange) {
      try {
        return increaseLiquidityQuoteByInputToken(
          token.mint,
          amount,
          tickRange[0],
          tickRange[1],
          SLIPPAGE,
          currentPool,
        )
      } catch (e) {
        console.error('e ', e)
        return null
      }
    }

  }, [currentPool, tickRange])

  const maxPriceByTokenPair = useCallback((maxTokenAAmount: Decimal, maxTokenBAmount: Decimal) => {
    if (currentPool) {
      const tokenAInfo = currentPool.getTokenAInfo()
      const tokenBInfo = currentPool.getTokenBInfo()
      const quoteA = quoteByToken(tokenAInfo, maxTokenAAmount)
      const quoteB = quoteByToken(tokenBInfo, maxTokenBAmount)

      // If the quote is not available, then we can't calculate a maximum price
      if(!quoteA || !quoteB) return [maxTokenAAmount, maxTokenBAmount]

      const quoteATokenAEstAmount = DecimalUtil.fromU64(quoteA?.tokenEstA, tokenAInfo.decimals)
      const quoteATokenBEstAmount = DecimalUtil.fromU64(quoteA?.tokenEstB, tokenBInfo.decimals)

      const quoteBTokenAEstAmount = DecimalUtil.fromU64(quoteB?.tokenEstA, tokenAInfo.decimals)
      const quoteBTokenBEstAmount = DecimalUtil.fromU64(quoteB?.tokenEstB, tokenBInfo.decimals)

      const totalMax = maxTokenAAmount.plus(maxTokenBAmount)

      if (totalMax.greaterThan(quoteATokenAEstAmount.plus(quoteATokenBEstAmount))) {
        setFieldNameOfMaxAmount('tokenAmountA')
        setMaxAmount(quoteATokenAEstAmount)
        return [quoteATokenAEstAmount, quoteATokenBEstAmount]
      } else {
        setFieldNameOfMaxAmount('tokenAmountB')
        setMaxAmount(quoteBTokenBEstAmount)
        return [quoteBTokenAEstAmount, quoteBTokenBEstAmount]
      }
    }
    return [maxTokenAAmount, maxTokenBAmount]
  }, [currentPool, quoteByToken])

  const maxApplicableTokenBalances = useMemo<Map<string, Decimal> | null>(() => {
    if (userBalances && ratios && tokenIn && tokenOut) {
      const [ratioA, ratioB] = ratios
      const tokenBalanceA = new Decimal(userBalances[tokenIn.symbol] || 0)
      const tokenBalanceB = new Decimal(userBalances[tokenOut.symbol] || 0)

      let tokenMaxValueA = tokenBalanceA.times(usdRateA)
      let tokenMaxValueB = tokenBalanceB.times(usdRateB)

      const totalMaxValueComputedByA = tokenMaxValueA.div(ratioA)
      const totalMaxValueComputedByB = tokenMaxValueB.div(ratioB)

      if (totalMaxValueComputedByA.greaterThan(totalMaxValueComputedByB)) {
        tokenMaxValueA = totalMaxValueComputedByB.minus(tokenMaxValueB)
      } else if (totalMaxValueComputedByB.greaterThan(totalMaxValueComputedByA)) {
        tokenMaxValueB = totalMaxValueComputedByA.minus(tokenMaxValueA)
      }

      const [tokenMaxAmountA, tokenMaxAmountB] = maxPriceByTokenPair(
        tokenMaxValueA.div(usdRateA),
        tokenMaxValueB.div(usdRateB),
      )

      const result = new Map([[tokenIn.symbol, tokenMaxAmountA], [tokenOut.symbol, tokenMaxAmountB]])
      return result
    }
    return null
  }, [userBalances, ratios, tokenIn, tokenOut, usdRateA, usdRateB, maxPriceByTokenPair])

  const submitOpenPosition = useCallback(async (values: OpenPositionFormValues, setSubmitting: (isSubmitting: boolean) => void) => {
    if (!publicKey) {
      connectWallet()
    } else if (currentPool && tickRange) {
      const tokenAInfo = currentPool.getTokenAInfo()
      const tokenBInfo = currentPool.getTokenBInfo()
      const { tickCurrentIndex } = currentPool.getData()
      const [tickLowerIndex, tickUpperIndex] = tickRange

      const initTickTx = await getInitTickArrayTx(
        client,
        currentPool,
        tickLowerIndex,
        tickUpperIndex,
      )

      let inputToken = tokenAInfo
      let inputTokenAmount = new Decimal(values.tokenAmountA)
      if (tickLowerIndex <= tickCurrentIndex) {
        inputToken = tokenBInfo
        inputTokenAmount = new Decimal(values.tokenAmountB)
      }

      const quote = increaseLiquidityQuoteByInputToken(
        inputToken.mint,
        inputTokenAmount,
        tickLowerIndex,
        tickUpperIndex,
        SLIPPAGE,
        currentPool,
      )

      setSubmitting(true)

      const { tx } = await currentPool.openPosition(
        tickLowerIndex,
        tickUpperIndex,
        quote,
        publicKey,
      )
      if (initTickTx) {
        tx.prependInstruction(initTickTx.compressIx(true))
      }
      const txid = await safelySendTxWithNotice(tx, {
        gasless: false,
        errorTitle: trans.liquidity.open_position.open_position_failed,
        successTitle: trans.liquidity.open_position.open_position_successfully,
      })

      if (txid) {
        setTimeout(() => {
          router.push('/liquidity/portfolio')
        }, 1000)
      }
    }
    setSubmitting(false)
  }, [
    client,
    connectWallet,
    currentPool,
    publicKey,
    router,
    safelySendTxWithNotice,
    tickRange,
    trans.liquidity.open_position.open_position_failed,
    trans.liquidity.open_position.open_position_successfully,
  ])

  const handleTokenAmountChange = useCallback(
    (currentFieldName: keyof OpenPositionFormValues, currentFieldStringValue: number | undefined) => {
      const { setFieldValue } = formRef?.current || {}
      if (setFieldValue) {
        if (currentPool) {
          const tokenAInfo = currentPool.getTokenAInfo()
          const tokenBInfo = currentPool.getTokenBInfo()

          const inputToken = currentFieldName === 'tokenAmountA' ? tokenAInfo : tokenBInfo
          const inputTokenAmount =  toDecimal(String(currentFieldStringValue || 0))
          const quote = quoteByToken(inputToken, inputTokenAmount)

          if (!quote) {
            if (currentFieldName === 'tokenAmountA') {
              setFieldValue('tokenAmountB', 0)
            } else if (currentFieldName === 'tokenAmountB') {
              setFieldValue('tokenAmountA', 0)
            }
            return
          }

          const tokenAEstAmount = DecimalUtil.fromU64(quote.tokenEstA, tokenAInfo.decimals)
          const tokenBEstAmount = DecimalUtil.fromU64(quote.tokenEstB, tokenBInfo.decimals)

          if (currentFieldName === 'tokenAmountA') {
            setFieldValue('tokenAmountB', tokenBEstAmount.toNumber())
          } else if (currentFieldName === 'tokenAmountB') {
            setFieldValue('tokenAmountA', tokenAEstAmount.toNumber())
          }
        }
      }
    },
    [currentPool, quoteByToken],
  )

  const setPriceRangeSafe = useCallback((minPrice: string, maxPrice: string, reverted: boolean) => {
    const [minValue, maxValue] = [minPrice, maxPrice].map((price) => {
      let priceDecimal: Decimal
      try {
        priceDecimal = new Decimal(price).pow(reverted ? -1 : 1)
      } catch (e) {
        priceDecimal = new Decimal(0)
      }
      return priceDecimal.isFinite() ? priceDecimal : new Decimal(0)
    })

    setPriceRange(reverted ? [maxValue, minValue] : [minValue, maxValue])
  }, [setPriceRange])

  const handleInputMaximumBalance = useCallback(
    () => {
      const { setFieldValue } = formRef?.current || {}
      if (!setFieldValue) return

      setFieldValue(fieldNameOfMaxAmount, maxAmount.toNumber())
      handleTokenAmountChange(fieldNameOfMaxAmount, maxAmount.toNumber())
    },
    [fieldNameOfMaxAmount, maxAmount, handleTokenAmountChange],
  )

  const onApply = useCallback(() => setIsOpenSetLiquidity(false), [])
  const onChangeRange = useCallback(() => setIsOpenSetLiquidity(true), [])

  useEffect(() => {
    if (!isOpenSetLiquidity) {
      formRef.current?.setValues(
        (values) => ({
          ...values,
          minPrice: toDecimal(formattedMinPrice).toFixed(),
          maxPrice: toDecimal(formattedMaxPrice).toFixed(),
        }),
        true,
      )
    }
  }, [formattedMaxPrice, formattedMinPrice, isOpenSetLiquidity])

  useEffect(() => {
    if (!isOpenSetLiquidity && formRef.current?.values.tokenAmountA) {
      handleTokenAmountChange('tokenAmountA', Number(formRef.current?.values.tokenAmountA))
    }
  }, [handleTokenAmountChange, isOpenSetLiquidity])

  if (!(tokenIn && tokenOut && currentPool)) return <ViewSkeleton />

  return (
    <Formik<OpenPositionFormValues>
      innerRef={formRef}
      initialValues={initialValues}
      validate={(values) => {
        const errors: FormikErrors<OpenPositionFormValues> = {}
        if (+values.maxPrice <= +values.minPrice) {
          errors.maxPrice = 'must_be_gt_min_price'
          errors.minPrice = 'must_be_lt_max_price'
        }
        if (isNaN(parseFloat(values.minPrice))) {
          errors.minPrice = 'must_be_a_number'
        }
        if (isNaN(parseFloat(values.maxPrice))) {
          errors.maxPrice = 'must_be_a_number'
        }
        return errors
      }}
      onSubmit={(values: OpenPositionFormValues, { setSubmitting }) => {
        submitOpenPosition(values, setSubmitting)
      }}
    >
      {(formProps) => {
        const computedProps = {
          ...formProps,
          handleTokenAmountChange,
          tokenIn,
          tokenOut,
          bPerAPrice,
          setPriceRange: setPriceRangeSafe,
          maxApplicableTokenBalances,
          ratios,
          usdRateA,
          usdRateB,
          walletConnected: !!publicKey,
          handleInputMaximumBalance,
          pricesReverted,
          setPricesReverted,
          onApply,
          onChangeRange,
          isOpenSetLiquidity,
          formattedMinPrice,
          formattedMaxPrice,
          insufficientBalanceMsg,
        }

        return <View {...computedProps} />
      }}
    </Formik>
  )
}

export default Container
